import { ChevronDownIcon } from '@heroicons/react/solid';
import useLocale from 'next-translate/useTranslation';
import React, { FC, memo, useEffect, useMemo, useRef, useState } from 'react';
import { FaCrown } from 'react-icons/fa6';

import clsxm from '@/lib/clsxm';

import { SimpleTooltip } from '@/components/ui/tooltip';

import {
  isPremiumEditModel,
  modelsWithDescription,
} from '@/constants/imageModels';

interface EditModelOption {
  value: string;
  label: string;
  credits?: number;
  resolutions?: string[];
}

function getModelMeta(value: string) {
  return modelsWithDescription.find((m) => m.title === value || m.id === value);
}

interface EditModelSelectorProps {
  value: string;
  options: EditModelOption[];
  onChange: (value: string) => void;
  isFreePlan?: boolean;
}

const EditModelSelector: FC<EditModelSelectorProps> = ({
  value,
  options,
  onChange,
  isFreePlan = false,
}) => {
  const { t } = useLocale('ai-images');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = useMemo(
    () => options.find((o) => o.value === value) ?? options[0],
    [options, value],
  );

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className='relative w-full'>
      <button
        type='button'
        onClick={() => setOpen((v) => !v)}
        aria-haspopup='listbox'
        aria-expanded={open}
        className='w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-left shadow-sm transition-all duration-200 hover:bg-gray-50 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700'
      >
        <div className='flex items-center justify-between'>
          <div className='min-w-0 flex-1'>
            <div className='mb-1 flex items-center gap-2'>
              <span
                translate='no'
                className='flex items-center truncate text-sm font-medium text-gray-900 dark:text-gray-100'
              >
                {(() => {
                  const Icon = getModelMeta(current?.value ?? value)?.icon;
                  return Icon
                    ? React.createElement(Icon, {
                        className: 'w-4 h-4 mr-2 flex-shrink-0',
                      })
                    : null;
                })()}
                <span translate='no'>{current?.label ?? value}</span>
              </span>
            </div>
            {(() => {
              const description = getModelMeta(
                current?.value ?? value,
              )?.description;
              return description ? (
                <p className='truncate text-xs text-gray-500 dark:text-gray-400'>
                  {t(description)}
                </p>
              ) : null;
            })()}
          </div>
          <ChevronDownIcon
            className={clsxm(
              'ml-2 h-5 w-5 flex-shrink-0 text-gray-400 transition-transform',
              open && 'rotate-180',
            )}
          />
        </div>
      </button>

      {open && (
        <div
          role='listbox'
          className='absolute left-0 right-0 z-20 mt-1 max-h-96 overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-white/10 dark:bg-slate-800'
        >
          {options.map((option) => {
            const isSelected = option.value === current?.value;
            const isLocked = isFreePlan && isPremiumEditModel(option.value);
            return (
              <button
                key={option.value}
                type='button'
                role='option'
                aria-selected={isSelected}
                disabled={isLocked}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={clsxm(
                  'flex w-full items-center justify-between px-4 py-2.5 text-left transition-colors',
                  isSelected
                    ? 'bg-primary-50 dark:bg-primary-900/20'
                    : isLocked
                      ? 'cursor-not-allowed'
                      : 'hover:bg-gray-50 dark:hover:bg-slate-700/60',
                )}
              >
                <div className='min-w-0 flex-1'>
                  <div
                    className={clsxm(
                      'flex items-center truncate text-sm font-medium',
                      isSelected
                        ? 'text-primary-700 dark:text-primary-200'
                        : isLocked
                          ? 'text-gray-400 dark:text-slate-600'
                          : 'text-gray-900 dark:text-gray-100',
                    )}
                  >
                    {(() => {
                      const Icon = getModelMeta(option.value)?.icon;
                      return Icon
                        ? React.createElement(Icon, {
                            className: clsxm(
                              'w-4 h-4 mr-2 flex-shrink-0',
                              isLocked && 'opacity-50',
                            ),
                          })
                        : null;
                    })()}
                    <span translate='no'>{option.label}</span>
                    {isLocked && (
                      <SimpleTooltip title={t('Upgrade to use this model')}>
                        <span>
                          <FaCrown className='ml-1.5 h-3.5 w-3.5 text-amber-400' />
                        </span>
                      </SimpleTooltip>
                    )}
                  </div>
                  {(() => {
                    const description = getModelMeta(option.value)?.description;
                    return description ? (
                      <p className='line-clamp-1 text-xs text-gray-500 dark:text-gray-400'>
                        {t(description)}
                      </p>
                    ) : null;
                  })()}
                </div>
                {isSelected && (
                  <svg
                    className='ml-2 h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-300'
                    viewBox='0 0 20 20'
                    fill='currentColor'
                  >
                    <path
                      fillRule='evenodd'
                      d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                      clipRule='evenodd'
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default memo(EditModelSelector);
